// "Get Prequalified" CTA in the Summary section's Finance tab. Tesla
// uses a lender form; we use a HubSpot Note + Contact upsert so the
// financing team can follow up with the customer's preferred lender
// (Synchrony et al.). The customer fills in the form, we POST to
// /api/financing/prequalify which upserts the contact + attaches a
// "💳 Configurator prequalification request" note.
//
// Name / email / phone come from the configurator's Details section
// (the button only enables AFTER Calculate, which requires those four
// fields), so we don't re-ask for them. We show a small "We'll contact
// you at …" confirmation line and skip straight to the financing-
// specific questions (credit tier / term / down).

import { useState } from "react";

import { api } from "../api";
import { signalPrequalifyRequested } from "../tracking";
import {
  CREDIT_APRS,
  CREDIT_TIER_LABELS,
  useConfigurator,
} from "../store";
import type { CreditTier } from "../store";
import { ContactSummary, Field, FormModal, inputClass } from "./FormModal";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function PrequalifyModal({ open, onClose }: Props) {
  const { state, selectedLineItems } = useConfigurator();
  const c = state.customer;
  const f = state.financing;

  // Financing-specific local state, seeded from the global financing
  // assumptions so the modal opens to "what the customer is looking at"
  // and they only adjust what they want to.
  const [creditTier, setCreditTier] = useState<CreditTier>(f.creditTier);
  const [termMonths, setTermMonths] = useState<60 | 72 | 84>(f.termMonths);
  const [downPaymentDollars, setDownPaymentDollars] = useState(
    Math.round(f.downPaymentCents / 100),
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !submitting && c.email.trim().length > 0;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.prequalify({
        customer: {
          first_name: c.firstName,
          last_name: c.lastName,
          email: c.email,
          phone: c.phone,
          address: c.address,
          city: c.city,
          state: c.state,
          zip: c.zip,
        },
        credit_tier: creditTier,
        term_months: termMonths,
        down_payment_cents: Math.max(100_000, downPaymentDollars * 100),
        line_items: selectedLineItems,
      });
      setSubmitted(true);
      // Strong buying signal to Emily — skip in employee mode (rep, not buyer).
      if (!state.employee.active) signalPrequalifyRequested();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not submit — please try again or call us.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    setSubmitted(false);
    setError(null);
    onClose();
  }

  return (
    <FormModal
      open={open}
      onClose={handleClose}
      title="Get Prequalified"
      subtitle="A Gammill specialist will connect you with our preferred lender for a soft-pull prequalification using your saved contact details."
    >
      {submitted ? (
        <div className="py-4">
          <p className="font-display text-lg font-extrabold uppercase tracking-wide text-ink">
            Got it.
          </p>
          <p className="mt-2 font-serif text-base italic text-graphite">
            We've routed your prequalification request to our financing team.
            You'll hear from a Gammill specialist within one business day.
          </p>
          <button
            type="button"
            onClick={handleClose}
            className="mt-4 inline-flex items-center justify-center rounded-full bg-amber px-5 py-2.5 font-sans text-sm font-bold tracking-[0.02em] text-ink shadow-[0_4px_12px_rgba(250,173,61,0.32)] transition hover:bg-amber-hover"
          >
            Close
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <ContactSummary customer={c} />

          <Field
            label="Estimated credit"
            hint="No credit pull here. This just helps the lender route your request."
            required
          >
            <select
              value={creditTier}
              onChange={(e) => setCreditTier(e.target.value as CreditTier)}
              className={inputClass}
            >
              {(Object.keys(CREDIT_APRS) as CreditTier[]).map((tier) => (
                <option key={tier} value={tier}>
                  {CREDIT_TIER_LABELS[tier]}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Loan term">
              <select
                value={termMonths}
                onChange={(e) =>
                  setTermMonths(Number(e.target.value) as 60 | 72 | 84)
                }
                className={inputClass}
              >
                <option value={60}>60 months</option>
                <option value={72}>72 months</option>
                <option value={84}>84 months</option>
              </select>
            </Field>
            <Field label="Down payment ($)" hint="$1,000 minimum">
              <input
                type="number"
                min={1000}
                step={500}
                value={downPaymentDollars}
                onChange={(e) =>
                  setDownPaymentDollars(Number(e.target.value || 0))
                }
                className={inputClass}
              />
            </Field>
          </div>

          {error && (
            <p className="my-3 border-l-[3px] border-red-400 pl-4 font-sans text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 font-sans text-sm font-semibold text-steel hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center justify-center rounded-full bg-amber px-5 py-2.5 font-sans text-sm font-bold tracking-[0.02em] text-ink shadow-[0_4px_12px_rgba(250,173,61,0.32)] transition hover:bg-amber-hover disabled:cursor-not-allowed disabled:bg-mist disabled:text-steel disabled:shadow-none"
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      )}
    </FormModal>
  );
}
